import React, { useState } from "react";

import { Layout } from "../../../layouts/DashboardLayout";
import { Payment, User } from "../../../utils/database";
import TransactionsList from "../../../components/common/lists/TransactionsList";
import usePaymentsByCategory, { PaymentCategory } from "../../../hooks/payments/usePaymentsByCategory";
import TransactionDetailsModal from "../../../components/common/details/TransactionDetails";

import { paymentsService } from "../../../services/paymentsService";
import useToken from "../../../../../hooks/auth/useToken";
import useUserPayments from "../../../hooks/payments/useUserPayments";
import usePaymentsByUser from "../../../hooks/payments/useUserPayments";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

interface FilterState {
  search: string;
  status: string;
  operator: string;
  provider: string;
  dateRange: string;
  startDate: string;
  endDate: string;
}

const UserTransactionsPage: React.FC = () => {
  // Auth token
  const { token } = useToken();
  const { userId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const user = location.state;

  // Récupération des transactions selon la catégorie (lazy: only active tab loads)
  const {
    payments: apiPayments,
    loading: paymentsLoading,
    getPayments: refreshPayments,
    total: totalTransactionsCount,
    currentPage: serverPage,
    totalPages: serverTotalPages,
    goToPage,
    handleSearch: serverSearch,
  } = usePaymentsByUser({
    userId: userId!,
    enabled: true,
  });

  // State management
  const [filters, setFilters] = useState<FilterState>({
    search: "",
    status: "",
    operator: "",
    provider: "",
    dateRange: "",
    startDate: "",
    endDate: "",
  });

  const [selectedTransaction, setSelectedTransaction] = useState<Payment | null>(null);

  // Client-side filtering for status/operator/provider (search is server-side)
  const filteredTransactions = apiPayments.filter((transaction) => {
    return (
      (filters.status === "" || transaction.status === filters.status) &&
      (filters.operator === "" || transaction.operator === filters.operator) &&
      (filters.provider === "" || transaction.provider === filters.provider)
    );
  });

  // Handle search filter change -> delegate to server
  const handleFiltersChange = (newFilters: FilterState) => {
    if (newFilters.search !== filters.search) {
      serverSearch(newFilters.search);
    }
    setFilters(newFilters);
  };

  // Pagination from server
  const startIndex = (serverPage - 1) * 20;
  const endIndex = Math.min(startIndex + filteredTransactions.length, totalTransactionsCount);

  // Gestionnaires d'événements
  const handleTransactionAction = async (action: string, transaction: Payment, data?: any) => {
    switch (action) {
      case "view":
        setSelectedTransaction(transaction);
        break;
      case "download":
        console.log("Télécharger reçu pour:", transaction.id);
        break;
      case "retry":
        console.log("Réessayer transaction:", transaction.id);
        break;
      case "change-status":
        try {
          const payoutStatus = data?.payout_status || "SUCCESS";
          const transactionStatus = data?.transaction_status || "SUCCEEDED";
          const response = await paymentsService.changePaymentStatus(
            token,
            transaction.id,
            payoutStatus,
            transactionStatus
          );
          if (response.message) {
            alert(`✅ ${response.message}`);
            // Rafraîchir les données
            refreshPayments();
          }
        } catch (error: any) {
          console.error("Erreur lors du changement de statut:", error);
          alert(`❌ Erreur: ${error.response?.data?.message || error.message || "Une erreur est survenue"}`);
        }
        break;
      case "load-verification-sides":
        return paymentsService.getVerificationSides(token, transaction.id);
      case "check-status": {
        const side = data?.side || "canada";
        return paymentsService.verifyOperatorStatus(token, transaction.id, side);
      }
    }
  };

  return (
    <Layout>
      <div className="mb-8 flex items-center gap-4">
        {/* Bouton retour */}
        <button
          onClick={() => navigate(-1)}
          className="flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors shadow-sm"
          aria-label="Retour"
        >
          <ArrowLeft className="w-5 h-5 text-gray-700" />
        </button>

        {/* Titre */}
        <h1 className="text-3xl font-bold text-gray-800 truncate">Transactions de {user?.name || "Utilisateur"}</h1>
      </div>

      {/* Liste des Transactions */}
      <TransactionsList
        transactions={filteredTransactions}
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onTransactionAction={handleTransactionAction}
        currentPage={serverPage}
        totalPages={serverTotalPages}
        onPageChange={goToPage}
        startIndex={startIndex}
        endIndex={endIndex}
        totalTransactions={totalTransactionsCount}
        loading={paymentsLoading}
      />

      {/* Modal de Détails */}
      <TransactionDetailsModal
        transaction={selectedTransaction}
        isOpen={!!selectedTransaction}
        onClose={() => setSelectedTransaction(null)}
        onTransactionUpdated={refreshPayments}
      />
    </Layout>
  );
};

export default UserTransactionsPage;
